import { Button, Col, Form, Row } from "react-bootstrap";
import { getJeepRallyMediaUrl } from "../../../../../api/jeep-rally/api";
import {
  emptyCheckpoint,
  MAX_CHECKPOINTS_PER_STAGE,
} from "../utils/checkpoint.utils";

const CheckpointImagePicker = ({
  checkpoint,
  disabled,
  onChange,
}) => {
  const preview =
    checkpoint.imagePreview ||
    getJeepRallyMediaUrl(checkpoint.image) ||
    "";

  const handlePick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled) return;

    const previewUrl = URL.createObjectURL(file);
    onChange({
      imageFile: file,
      imagePreview: previewUrl,
      image: checkpoint.image || "",
    });
  };

  const clearImage = () => {
    if (checkpoint.imagePreview) {
      try {
        URL.revokeObjectURL(checkpoint.imagePreview);
      } catch {
        /* ignore */
      }
    }
    onChange({ imageFile: null, imagePreview: "", image: "" });
  };

  return (
    <Form.Group className="mb-0">
      <Form.Label>Image</Form.Label>
      <div className="d-flex gap-2 align-items-start flex-wrap">
        <div
          className="d-flex align-items-center justify-content-center overflow-hidden border rounded"
          style={{ width: 88, height: 64, flexShrink: 0 }}
        >
          {preview ? (
            <img
              src={preview}
              alt={checkpoint.title || "Checkpoint"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span className="text-muted small">No image</span>
          )}
        </div>
        <div>
          <Form.Control
            type="file"
            accept="image/*"
            size="sm"
            disabled={disabled}
            onChange={handlePick}
          />
          <Form.Text className="text-muted d-block">
            Uploaded after checkpoints are saved (API requires checkpoint id).
          </Form.Text>
          {(checkpoint.image || checkpoint.imageFile) && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0"
              disabled={disabled}
              onClick={clearImage}
            >
              Clear image
            </Button>
          )}
        </div>
      </div>
    </Form.Group>
  );
};

const StageCheckpointsEditor = ({
  stage,
  stageIndex,
  categories = [],
  isCategoriesLoading = false,
  disabled = false,
  errors = {},
  onChange,
  hideCategorySelect = false,
}) => {
  const checkpoints = stage.checkpoints || [];
  const canAdd = checkpoints.length < MAX_CHECKPOINTS_PER_STAGE;

  const updateCheckpoints = (next) => {
    onChange({ checkpoints: next });
  };

  const updateCheckpoint = (index, patch) => {
    updateCheckpoints(
      checkpoints.map((cp, i) => (i === index ? { ...cp, ...patch } : cp))
    );
  };

  const addCheckpoint = () => {
    if (!canAdd) return;
    const nextOrder = checkpoints.length + 1;
    updateCheckpoints([...checkpoints, emptyCheckpoint(nextOrder)]);
  };

  const removeCheckpoint = (index) => {
    const removed = checkpoints[index];
    if (removed?.imagePreview) {
      try {
        URL.revokeObjectURL(removed.imagePreview);
      } catch {
        /* ignore */
      }
    }
    updateCheckpoints(
      checkpoints
        .filter((_, i) => i !== index)
        .map((cp, i) => ({ ...cp, order: i + 1 }))
    );
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Checkpoints</h6>
        <span className="text-muted small">
          {checkpoints.length}/{MAX_CHECKPOINTS_PER_STAGE}
        </span>
      </div>
      <p className="text-muted small mb-3">
        Optional. Max {MAX_CHECKPOINTS_PER_STAGE} per stage. Saved via bulk
        replace for this stage + category.
      </p>

      {!hideCategorySelect ? (
        <Form.Group className="mb-3">
          <Form.Label>
            Category for checkpoints
            {checkpoints.length > 0 ? (
              <span className="text-danger"> *</span>
            ) : null}
          </Form.Label>
          <Form.Select
            value={stage.checkpoint_category_id || ""}
            onChange={(e) =>
              onChange({ checkpoint_category_id: e.target.value })
            }
            disabled={disabled || isCategoriesLoading}
            isInvalid={!!errors.checkpoint_category_id}
          >
            <option value="">Select category</option>
            {categories.map((category) => {
              const id = category._id || category.id;
              return (
                <option key={id} value={id}>
                  {category.title || category.key || id}
                </option>
              );
            })}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {errors.checkpoint_category_id}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Backend stores checkpoints per stage + category. Prefer a category
            linked to this route.
          </Form.Text>
        </Form.Group>
      ) : null}

      {errors.checkpoints && (
        <div className="text-danger small mb-2">{errors.checkpoints}</div>
      )}

      {checkpoints.length === 0 ? (
        <p className="text-muted small mb-2">No checkpoints yet.</p>
      ) : (
        checkpoints.map((cp, index) => {
          const itemError = errors.checkpointItems?.[index] || {};
          return (
            <div
              key={cp.localKey || cp._id || `stage-${stageIndex}-cp-${index}`}
              className="card mb-2"
            >
              <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="small">Checkpoint {index + 1}</strong>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  disabled={disabled}
                  onClick={() => removeCheckpoint(index)}
                >
                  Remove
                </Button>
              </div>

              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Title <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={cp.title}
                      onChange={(e) =>
                        updateCheckpoint(index, { title: e.target.value })
                      }
                      placeholder="CP1 — Desert Gate"
                      isInvalid={!!itemError.title}
                      disabled={disabled}
                    />
                    <Form.Control.Feedback type="invalid">
                      {itemError.title}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Km start</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={cp.km_start}
                      onChange={(e) =>
                        updateCheckpoint(index, { km_start: e.target.value })
                      }
                      isInvalid={!!itemError.km_start}
                      disabled={disabled}
                    />
                    <Form.Control.Feedback type="invalid">
                      {itemError.km_start}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Km end</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={cp.km_end}
                      onChange={(e) =>
                        updateCheckpoint(index, { km_end: e.target.value })
                      }
                      isInvalid={!!itemError.km_end}
                      disabled={disabled}
                    />
                    <Form.Control.Feedback type="invalid">
                      {itemError.km_end}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description points</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={cp.description_points_text}
                      onChange={(e) =>
                        updateCheckpoint(index, {
                          description_points_text: e.target.value,
                        })
                      }
                      placeholder={"One point per line\nTurn left at marker"}
                      disabled={disabled}
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id={`stage-${stageIndex}-cp-${index}-start`}
                    label="Is start"
                    checked={Boolean(cp.is_start)}
                    disabled={disabled}
                    onChange={(e) =>
                      updateCheckpoint(index, { is_start: e.target.checked })
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id={`stage-${stageIndex}-cp-${index}-finish`}
                    label="Is finish"
                    checked={Boolean(cp.is_finish)}
                    disabled={disabled}
                    onChange={(e) =>
                      updateCheckpoint(index, { is_finish: e.target.checked })
                    }
                  />
                </Col>

                <Col md={6}>
                  <CheckpointImagePicker
                    checkpoint={cp}
                    disabled={disabled}
                    onChange={(patch) => updateCheckpoint(index, patch)}
                  />
                </Col>
              </Row>
              </div>
            </div>
          );
        })
      )}

      <Button
        type="button"
        variant="outline-success"
        size="sm"
        disabled={disabled || !canAdd}
        onClick={addCheckpoint}
      >
        + Add Checkpoint
      </Button>
      {!canAdd && (
        <span className="text-muted small ms-2">
          Maximum {MAX_CHECKPOINTS_PER_STAGE} reached
        </span>
      )}
    </div>
  );
};

export default StageCheckpointsEditor;
